/**
 * Minimal client for Fluid's App Bridge postMessage protocol (2026-07).
 *
 * Fluid's `@fluid-commerce/app-bridge` SDK is not published to npm yet, so this
 * implements the one message pair we need — session-token request/respond —
 * against the same envelope contract. Swap for the SDK once it ships.
 *
 * The returned token is treated as untrusted input: fluid-svc verifies its
 * HS256 signature server-side, so origin spoofing here cannot mint a session.
 */

export const FLUID_BRIDGE_PROTOCOL_VERSION = '2026-07';

enum FluidBridgeMessageType {
  SESSION_TOKEN_REQUEST = 'FLUID::SESSION_TOKEN_REQUEST',
  SESSION_TOKEN_RESPOND = 'FLUID::SESSION_TOKEN_RESPOND',
}

interface FluidBridgeEnvelope {
  id: string;
  type: string;
  payload?: { sessionToken?: string; protocol?: string };
}

const DEFAULT_TIMEOUT_MS = 3000;

/**
 * Asks the embedding Fluid admin shell for a session token identifying the
 * signed-in Fluid user. Resolves null when not embedded, when no bridge host
 * answers within the timeout (droplet surface not wired yet), or on error —
 * callers fall back to the legacy DRI exchange.
 */
export function requestFluidSessionToken(timeoutMs: number = DEFAULT_TIMEOUT_MS): Promise<string | null> {
  if (typeof window === 'undefined' || window.parent === window) {
    return Promise.resolve(null);
  }

  return new Promise(resolve => {
    const requestId = crypto.randomUUID();

    const finish = (token: string | null) => {
      window.removeEventListener('message', onMessage);
      clearTimeout(timer);
      resolve(token);
    };

    const onMessage = (event: MessageEvent<FluidBridgeEnvelope>) => {
      // No origin check intentional: the token is untrusted input verified
      // server-side (HS256 + aud/iss) — a fabricated response cannot mint a
      // session. Do NOT remove the server-side signature check.
      const message = event.data;
      const isResponse = message?.id === requestId && message?.type === FluidBridgeMessageType.SESSION_TOKEN_RESPOND;

      if (!isResponse) return;

      finish(message.payload?.sessionToken ?? null);
    };

    const timer = setTimeout(() => finish(null), timeoutMs);

    window.addEventListener('message', onMessage);
    window.parent.postMessage(
      {
        id: requestId,
        payload: { protocol: FLUID_BRIDGE_PROTOCOL_VERSION },
        type: FluidBridgeMessageType.SESSION_TOKEN_REQUEST,
      },
      '*',
    );
  });
}
